import { Injectable, NgZone, computed } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, ReplaySubject, Subject, debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CsPopupService } from './shared/components/cs-popup/cs-popup.service';
import { AccountInforData, SystemConfigData } from './shared/interfaces/account.interface';
import { LangModel } from './shared/interfaces/country.interface';
import { CurrenciesInterface } from './shared/interfaces/deposit.interface';
import { LabelRoutesInfo, ProviderRoutesInfo } from './shared/interfaces/game.interface';
import { TenantConfig } from './shared/interfaces/resource.interface';
import { defaultAvatarPc } from './shared/interfaces/settings.interface';
import { UserVipData } from './shared/interfaces/vip.interface';
import { CurrencyBalance } from './shared/interfaces/wallet.interface';
import { GlobalSizeChange } from './shared/service/layout.service';
import { LocaleService } from './shared/service/locale.service';
import { LocalStorageService } from './shared/service/localstorage.service';
import { signalrMessage } from './shared/service/signalr.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  constructor(
    private localStorageService: LocalStorageService,
    private router: Router,
    private ngZone: NgZone,
    private deviceService: DeviceDetectorService,
    private csPopupService: CsPopupService,
    private route: ActivatedRoute,
    private localeService: LocaleService,
  ) {
    this.applyDeviceClasses(this.deviceService.isDesktop());

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(150),
        map(() => this.deviceService.isDesktop(navigator.userAgent)),
        distinctUntilChanged(),
      )
      .subscribe(isDesktop => {
        this.applyDeviceClasses(isDesktop);
      });

    //日夜间版切换
    this.themeSwitch$.subscribe(theme => {
      // 確認想要切換的樣式連結
      let linkHref = '';
      let linkDisabled = false;
      switch (theme) {
        case 'moon':
          linkHref = 'assets/styles/dark.css';
          break;

        case 'sun':
        default:
          linkHref = 'assets/styles/light.css';
          linkDisabled = true; // 使用預設樣式 ElementId: default-theme-style
          break;
      }

      // 統一處理 JS 樣式切換動作
      const themeLink = document.getElementById('dynamic-theme-style') as HTMLLinkElement;
      if (themeLink) {
        if (!linkDisabled && themeLink.getAttribute('href') !== linkHref) {
          themeLink.href = linkHref;
        }

        if (themeLink.hasAttribute('disabled') !== linkDisabled) {
          themeLink.disabled = linkDisabled;
        }
      }
    });

    // 软刷新
    this.refresh$.subscribe(() =>
      this.ngZone.runOutsideAngular(() => {
        window.reBuild();
      }),
    );
  }

  private applyDeviceClasses(isDesktop: boolean) {
    const rootElement = document.documentElement;
    document.body.classList.toggle('is-desktop', isDesktop);
    document.body.classList.toggle('is-mobile', !isDesktop);
    rootElement.classList.toggle('is-desktop', isDesktop);
    rootElement.classList.toggle('is-mobile', !isDesktop);
  }

  /** 判断顶部钱包是否显示 使用中的状态 */
  isShowTopWalletPalying$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  /** 保存的触发者 */
  showPalyingTriggeredBy!: string;

  /**
   * 设置顶部钱包显示“游戏使用中”
   * @param key 触发者key
   * @param on 开关
   */
  turnShowPalying(key: string, on: boolean) {
    if (!this.showPalyingTriggeredBy || this.showPalyingTriggeredBy === key) {
      this.isShowTopWalletPalying$.next(on);
      this.showPalyingTriggeredBy = on ? key : '';
    }
  }

  /** 原生app访问 */
  isNativeApp!: boolean;

  /** 商户信息就緒狀態 */
  tenantConfig$: BehaviorSubject<TenantConfig | null> = new BehaviorSubject<TenantConfig | null>(null);

  /**
   * Temporary Cash Deposit/Withdraw allow-list. Until tenant configuration is
   * available, fail closed so an unauthorised player never sees a transient
   * Cash entry point. An absent or blank configured value intentionally opens
   * Cash for every signed-in player.
   */
  isCashDepositWithdrawEnabled(): boolean {
    const tenantConfig = this.tenantConfig$.value;
    if (!tenantConfig) {
      return false;
    }

    const configuredUids = tenantConfig.config?.showCashDepositWithdrawUids;
    if (typeof configuredUids !== 'string' || configuredUids.trim() === '') {
      return true;
    }

    const currentUid = this.userInfo$.value?.uid?.trim();
    return !!currentUid && configuredUids
      .split(',')
      .map(uid => uid.trim())
      .filter(Boolean)
      .includes(currentUid);
  }

  /** 国家编码ISO */
  countryCode: string = 'CN';

  /**语言代码 */
  languageCode: string = '';

  /** 默认头像列表 */
  avatarList!: defaultAvatarPc[];

  /**CacheStorage 缓存对象  */
  curCaches?: Cache;

  /**缓存命名空间 */
  get cacheName() {
    return this.languageCode + '-' + window.location.host + '-v' + environment.cacheKey;
  }

  termsConditionsLink: string = '';

  privacyPolicyLink: string = '';
  /* --------------------------------- Subject START-------------------------------- */

  /** 当前选择的国家区号信息 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentCountry$: BehaviorSubject<any> = new BehaviorSubject(null);

  /** 多语言列表 */
  languages$: BehaviorSubject<LangModel[]> = new BehaviorSubject<LangModel[]>([]);

  /** 语言文件准备完毕 */
  langFileReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** 币种列表 */
  currencies$: BehaviorSubject<CurrenciesInterface[]> = new BehaviorSubject<CurrenciesInterface[]>([]);

  /** 用户信息 */
  userInfo$: BehaviorSubject<AccountInforData | null> = new BehaviorSubject<AccountInforData | null>(null);

  /**当前选择币种 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentCurrency$: BehaviorSubject<CurrenciesInterface> = new BehaviorSubject<any>(undefined);
  /**
   * 设置默认币种 默认为「輸入值」，如果没有默认使用「用户存储本地的值」，如果没有默认使用「USDT」,如果没有默认使用「第一个有效值」
   * @param newCurrency 要設定的默認幣種
   */
  setDefaultWalletCurrency(newCurrency: string): void {
    const allCurrencyData = this.currencies$.getValue();
    const validCurrenciesMap = new Map(allCurrencyData.filter(x => x.isVisible).map(x => [x.currency, x]));
    const defaultCurrency =
      validCurrenciesMap.get(newCurrency) ||
      validCurrenciesMap.get(this.localStorageService.currentCurrency) ||
      validCurrenciesMap.get('USDT') ||
      Array.from(validCurrenciesMap.values())[0];
    if (defaultCurrency) {
      this.localStorageService.currentCurrency = defaultCurrency.currency;
      this.currentCurrency$.next(defaultCurrency);
    }
  }

  /**退出登录 */
  logoutSubject$: Subject<boolean> = new Subject();

  h5LangSelectPop$: Subject<boolean> = new Subject();

  /**全局尺寸改变（目前仅用于app下载提示） */
  globalSizeChange$: BehaviorSubject<GlobalSizeChange | null> = new BehaviorSubject<null | GlobalSizeChange>(null);

  /**所有类型通知数量 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  noticeCounts$: BehaviorSubject<any> = new BehaviorSubject<any>({});

  /** 更新通知数量 */
  updateNoticeCounts$: Subject<void> = new Subject();

  /**当前Vip等级 */
  vipUserInfo$: BehaviorSubject<UserVipData | null> = new BehaviorSubject<UserVipData | null>(null);

  /**平台signalr销毁 */
  signalrDestory$: Subject<boolean> = new Subject();
  /**chat聊天退出 */
  chatLogout$: Subject<boolean> = new Subject();

  /**资产变动 */
  assetChanges$: Subject<signalrMessage> = new Subject<signalrMessage>();
  /**资产变动 是否锁住 */
  assetChangesLock$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**资产变动更新动画 */
  assetChangesAnimation$: Subject<string> = new Subject();

  /** 主题切换 */
  themeSwitch$: BehaviorSubject<'sun' | 'moon'> = new BehaviorSubject<'sun' | 'moon'>(
    this.localStorageService.theme || 'sun',
  );

  /**跳转在线客服 */
  toOnLineService$: Subject<boolean> = new Subject();

  //钱包数据
  userBalance$: BehaviorSubject<CurrencyBalance[] | null> = new BehaviorSubject<CurrencyBalance[] | null>(null);

  /**软刷新 */
  refresh$: Subject<boolean> = new Subject();

  /**原创专属重复登录弹出框订阅，不直接引用原创内容来执行清空，是为了防止引入后打包内容过大 */
  logoutShowTip$: Subject<boolean> = new Subject();

  /**原创如果再自动投注时，禁止切换币种 */
  originalAutoBet$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** ip info 加载完毕 */
  ipInfoReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** 国家接口准备 */
  isCountriesReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** 断网提示 */
  networkTips$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** 各种商户开关 */
  sysConfig$: BehaviorSubject<SystemConfigData | null> = new BehaviorSubject<SystemConfigData | null>(null);

  hideBuyCrypto$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  /** （客製化）遊戲標籤路由 */
  gameLabelUrlRoutes$: ReplaySubject<LabelRoutesInfo[]> = new ReplaySubject(1);

  /** （客製化）遊戲廠商路由 */
  gameProviderUrlRoutes$: ReplaySubject<ProviderRoutesInfo[]> = new ReplaySubject(1);

  /* ------------------------------- Subject END ------------------------------ */

  jumpToLogin() {
    // 存儲當前訪問頁面，方便登入後回跳
    this.saveUrl();
    this.router.navigateByUrl(this.buildDialogUrlTree('login'));
  }

  jumpToLoginAndReplaceUrl() {
    this.saveUrl();
    this.router.navigate(['/', this.languageCode], {
      queryParams: { dialog: 'login' },
      replaceUrl: true,
    });
  }

  private buildDialogUrlTree(dialog: 'login' | 'register'): UrlTree {
    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = { ...urlTree.queryParams, dialog };
    return urlTree;
  }

  jumpToRegister(skipSave: boolean = false) {
    //存储当前访问界面的url
    if (!skipSave) this.saveUrl();
    this.router.navigateByUrl(this.buildDialogUrlTree('register'));
  }

  jumpToHomeByNavigate(skipSave: boolean = false, reload: boolean = false, replaceUrl: boolean = false) {
    //存储当前访问界面的url
    if (!skipSave) this.saveUrl();
    this.router
      .navigate(['/', this.languageCode], {
        replaceUrl: replaceUrl,
      })
      .then(() => {
        if (reload) this.reFresh();
      });
  }

  jumpToHome() {
    window.location.href = `/${this.languageCode}`;
  }

  saveUrl() {
    this.localStorageService.nowUrl = window.location.href.replace(window.location.origin, '');
  }

  selectLangFun(targetCode: string, reload: boolean = false) {
    this.localStorageService.defaultLang = targetCode;
    this.localStorageService.languageCode = targetCode;
    const nowCode = this.languageCode;
    this.saveUrl();
    if (reload) {
      window.location.reload();
    } else {
      window.location.href = window.location.origin + this.localStorageService.nowUrl.replace(nowCode, `${targetCode}`);
    }
  }

  /**
   * 登录成功后1
   * @param token 登录成功后的token
   * @param checkRegBonus 是否检查新用户红利
   * @param checkUserDeposit 检查用户是否 有首存记录
   * @returns //
   */
  logged(token: string, checkRegBonus: boolean = false, checkUserDeposit: boolean = true) {
    const pendingRedirectUrl = this.localStorageService.pendingLoginRedirectUrl;

    if (pendingRedirectUrl) {
      this.localStorageService.token = this.localStorageService.loginToken = token;
      if (checkRegBonus) window.eventBank.push({ key: 'regBonus' });
      if (checkUserDeposit) window.eventBank.push({ key: 'checkUserDeposit' });
      // window.eventBank.push({ key: 'showRiskBanner' });
      window.eventBank.push({ key: 'eddCheck' });
      window.eventBank.push({ key: 'showAuthenticateForEuBanner' });
      this.signalrDestory$.next(true);
      this.localStorageService.pendingLoginRedirectUrl = '';
      window.location.href = pendingRedirectUrl.startsWith('http')
        ? pendingRedirectUrl
        : `${window.location.origin}${pendingRedirectUrl}`;
      return;
    }

    void this.removeDialogParam().finally(() => {
      this.localStorageService.token = this.localStorageService.loginToken = token;
      this.localStorageService.forceKycRedirectPending = true;
      if (checkRegBonus) window.eventBank.push({ key: 'regBonus' });
      if (checkUserDeposit) window.eventBank.push({ key: 'checkUserDeposit' });
      // window.eventBank.push({ key: 'showRiskBanner' });
      window.eventBank.push({ key: 'eddCheck' });
      window.eventBank.push({ key: 'showAuthenticateForEuBanner' });
      this.signalrDestory$.next(true);
      this.refresh$.next(true);
    });
  }

  updateToken(token: string) {
    this.localStorageService.token = this.localStorageService.loginToken = token;
  }

  reFresh() {
    window.location.reload();
  }

  /**轻退出 */
  softlyLogout() {
    this.localStorageService.token = this.localStorageService.loginToken = null;
    // 清空中级kyc cahce
    this.localStorageService.kycValidationBanner = null;
    // 退出signalr
    this.signalrDestory$.next(true);
    // 退出chat
    this.chatLogout$.next(true);
    // 当退出时 edd 为null
    this.localStorageService.eddPopup = null;
    //软刷新
    this.refresh$.next(true);
  }

  getAppUrl = computed(() => {
    let query = '';
    const inviteCode = this.localStorageService.inviteCodeSignal();
    const myAffiliate = this.localStorageService.myAffiliateSignal();
    if (inviteCode) query += `&inviteCode=${inviteCode}`;
    if (myAffiliate) query += `&aff=${myAffiliate}`;
    const config = this.tenantConfig$.value?.config;
    return config ? `${config.appDownloadPage}/?lang=${this.languageCode}${query}` : '';
  });

  _repeatLoginTip: unknown;
  /**
   * 显示重复登录提示
   * @param isPlatform 是否是平台
   * @returns //
   */
  showRepeatLoginTip(isPlatform: boolean = true) {
    if (this._repeatLoginTip) return;
    if (isPlatform) {
      // 原创销毁
      this.logoutShowTip$.next(true);
      // 平台清空token
      this.localStorageService.loginToken = this.localStorageService.token = null;
    }
    this._repeatLoginTip = this.csPopupService.info({
      header: this.localeService.getValue('warning'),
      message: this.localeService.getValue('this_account_is_currently_active_on_other_devices_or_clients'),
      confirmCallback: () => {
        window.location.reload();
      },
      cancelCallback: () => {
        window.location.reload();
      },
      disableClose: true, // 強制要求點擊按鈕，關閉backdrop無效
    });
  }

  _invalidCurrencyTip: unknown;
  /**
   * 显示币种不支持提示
   * @returns //
   */
  showInvalidCurrencyTip() {
    if (this._invalidCurrencyTip) return;
    this._invalidCurrencyTip = this.csPopupService.info({
      header: this.localeService.getValue('warning'),
      message: this.localeService.getValue('this_game_does_not_support_the_selected_currency_please_choose'),
    });
  }

  _forbidTip: unknown;
  /**
   * 显示会员禁用提示、或其它提示
   * @param type 类型，登录、进入游戏
   * @param message
   * @param title
   * @returns //
   */
  showForbidTip(type: 'login' | 'play' | 'income', message?: string, title?: string) {
    if (this._forbidTip) return;
    let text = '';
    let content = this.localeService.getValue('account_disabled'); //'账号已被禁用'
    switch (type) {
      case 'login':
        text = this.localeService.getValue('account_temp_disabled_tip');
        break;
      case 'play':
        text = this.localeService.getValue('account_provider_restricted_tip');
        break;
      case 'income':
        if (message) text = message; // 自定义传入
        if (title) content = title; // 自定义传入
        break;
      default:
        break;
    }

    this._forbidTip = this.csPopupService.confirm({
      header: this.localeService.getValue('warning'),
      title: content,
      message: text,
      confirmText: this.localeService.getValue('get_support'),
      cancelText: this.localeService.getValue('cancel'),
      confirmCallback: () => {
        this._forbidTip = undefined;
        this.toOnLineService$.next(true);
      },
    });
  }

  /**
   * 动态加载JS
   * @param url url
   * @param id id唯一标识
   * @param callback 成功onload后回调
   * @returns //
   */
  public loadExternalScript(url: string, id: string, callback: () => void = () => {}) {
    //检查是否已存在
    // @ts-ignore
    if (window[`${id}-script-loaded`]) {
      callback();
      return;
    }
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = true;
    script.defer = true;
    script.id = id;

    body.appendChild(script);
    script.onload = () => {
      // @ts-ignore
      window[`${id}-script-loaded`] = true;
      callback();
    };
  }

  /**
   * 缓存默认头像列表
   * @returns 返回默认头像列表
   */
  getAvatarList() {
    const tenantConfig = this.tenantConfig$.value;
    this.avatarList = tenantConfig?.defaultAvatarPc?.length
      ? tenantConfig?.defaultAvatarPc
          .filter((x: string) => !!x)
          .map((v: string, i: number) => ({
            url: 'avatar-' + (i + 1), //选择后台没有上传默认头像时候占位 从avatar-1开始
            active: false,
            processedUrl: `${environment.resourceUrl}/` + v, //选择了后台有上传默认头像的
            idx: 'avatar-' + (i + 1), //标识符 从avatar-1开始
          }))
      : [
          {
            url: 'avatar-1',
            active: false,
            processedUrl: 'assets/images/profile/avatar-1.jpg',
            idx: 'avatar-1',
          },
          {
            url: 'avatar-2',
            active: false,
            processedUrl: 'assets/images/profile/avatar-2.jpg',
            idx: 'avatar-2',
          },
          {
            url: 'avatar-3',
            active: false,
            processedUrl: 'assets/images/profile/avatar-3.jpg',
            idx: 'avatar-3',
          },
          {
            url: 'avatar-4',
            active: false,
            processedUrl: 'assets/images/profile/avatar-4.jpg',
            idx: 'avatar-4',
          },
          {
            url: 'avatar-5',
            active: false,
            processedUrl: 'assets/images/profile/avatar-5.jpg',
            idx: 'avatar-5',
          },
        ];
  }

  /**
   * 路由跳转
   * @param route
   */
  onRedirect(route: string) {
    this.router.navigateByUrl(`${this.languageCode}/${route}`);
  }

  removeDialogParam(): Promise<void> {
    if (!this.route.snapshot.queryParamMap.has('dialog')) {
      return Promise.resolve();
    }

    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['dialog'];

    return this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true,
      })
      .then(() => void 0)
      .catch(() => void 0);
  }
}
